import { useExperimentFlag } from './experiment-context';

export const Agent = {
  INSIGHTS: 'insights',
  DATA_ANALYSIS: 'dataAnalysis',
  MEDIA_PLANNING: 'mediaPlanning',
  DASHBOARD_CHARTS: 'dashboardCharts',
  INCREMENTALITY: 'incrementality',
  MMM: 'mmm',
  AUDIT: 'audit',
  ORACLE: 'oracle',
  SEARCH_PLANNING: 'searchPlanning',
  SOCIAL_PLANNING: 'socialPlanning'
} as const;
export type AgentType = (typeof Agent)[keyof typeof Agent];

// Agents whose backend implements cooperative cancel (`forwardedProps.mode === 'cancel'`). An agent
// outside this set would treat a cancel request as a normal run, so its chat shows no Stop control.
const AGENTS_SUPPORTING_CANCEL: ReadonlySet<AgentType> = new Set<AgentType>([Agent.DATA_ANALYSIS]);

export const supportsCancel = (agent: AgentType): boolean => AGENTS_SUPPORTING_CANCEL.has(agent);

// Dev runtime ARNs are used when running locally and for prod users in the
// `useDevelopmentAgents` experiment. Prod runtime ARNs are used by everyone
// else in deployed environments.
const AGENT_ENDPOINTS: Record<AgentType, { dev: string; prod: string }> = {
  [Agent.INSIGHTS]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_insights_agent_dev-pXRryYGp5w/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_insights_agent_prod-mbViwyC4ar/invocations?qualifier=DEFAULT'
  },
  [Agent.DATA_ANALYSIS]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_data_analytics_agent_dev-YPjPXBCdpC/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_data_analytics_agent_prod-tsM49c4Aca/invocations?qualifier=DEFAULT'
  },
  [Agent.MEDIA_PLANNING]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_tvad_mediaplanning_agent_dev-2hMa4WHkmo/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_tvad_mediaplanning_agent_prod-uDbArx47LD/invocations?qualifier=DEFAULT'
  },
  [Agent.DASHBOARD_CHARTS]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_dashboard_charts_dev-H8M2rdFule/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_dashboard_charts_prod-1kb07PDUDm/invocations?qualifier=DEFAULT'
  },
  [Agent.INCREMENTALITY]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_incrementality_agent_dev-ebNVg2EoiY/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_incrementality_agent_prod-321WL26kse/invocations?qualifier=DEFAULT'
  },
  [Agent.MMM]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_mmm_agent_dev-c7L5lH5Dr5/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_mmm_agent_prod-3u8gn2APeq/invocations?qualifier=DEFAULT'
  },
  [Agent.AUDIT]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Fblisspoint_audit_agent_dev-VK7SBiGssY/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Fblisspoint_audit_agent_prod-Kos1c6G2pJ/invocations?qualifier=DEFAULT'
  },
  [Agent.ORACLE]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_oracle_agent_dev_agui_strands_agent-fAHmsd4zir/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_oracle_agent_prod_agui_strands_agent-pvgD039RlW/invocations?qualifier=DEFAULT'
  },
  [Agent.SEARCH_PLANNING]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_search_agent_dev-uqWi2F6njN/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_search_agent_prod-oJ6pmXClyo/invocations?qualifier=DEFAULT'
  },
  [Agent.SOCIAL_PLANNING]: {
    dev: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A975049949153%3Aruntime%2Ftinuiti_social_agent_dev-q3ntqt70KB/invocations?qualifier=DEFAULT',
    prod: 'https://bedrock-agentcore.us-west-2.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-west-2%3A625555678634%3Aruntime%2Ftinuiti_social_agent_prod-mgcHJPAhAT/invocations?qualifier=DEFAULT'
  }
};

// Picks between a dev and a prod endpoint for a per-stage-URL pair -- kept in one place so every
// caller uses the same selection rule.
// Production builds set `VITE_STAGE=production` and staging builds set `staging`; local `bun dev`
// interpolates `$BPM_STAGE` (typically unset), so anything else is treated as local dev. Staging
// uses its own `staging` URL when one is given and otherwise falls back to `prod` (there are no
// staging runtimes yet). Users opted into `useDevelopmentAgents` always get the dev URL.
const useStageEndpoint = ({
  dev,
  staging,
  prod
}: {
  dev: string;
  staging?: string;
  prod: string;
}): string => {
  const useDevelopmentAgents = Boolean(useExperimentFlag('useDevelopmentAgents'));
  const stage = import.meta.env.VITE_STAGE;
  if (useDevelopmentAgents) {
    return dev;
  }
  if (stage === 'production') {
    return prod;
  }
  if (stage === 'staging') {
    return staging ?? prod;
  }
  return dev;
};

export const useAgentEndpoint = (agent: AgentType): string =>
  useStageEndpoint(AGENT_ENDPOINTS[agent]);

// True when the page talks to the agent's dev runtime (local builds, and users in the
// `useDevelopmentAgents` experiment). Lets a host show a feature only its dev runtime supports yet.
export const useIsDevelopmentAgent = (agent: AgentType): boolean =>
  useAgentEndpoint(agent) === AGENT_ENDPOINTS[agent].dev;
